import React, { useMemo, memo } from 'react';
import { CALLS } from '../../../utils/search/constants';
import {
  ResultsContainer as StyledResultsContainer,
  OverflowWrapper,
} from './ConnectedUniversalSearch.styles';
import type {
  HandleAxonSearchResult,
  ResultsContainerProps,
  SearchResult,
  SearchResultData,
} from './ConnectedUniversalSearch.types';
import { ErrorWrapper } from './ErrorWrapper';
import StyledAxon from './styledDropdown/StyledAxon';
import StyledCall from './styledDropdown/StyledCall';
import StyledOutboundSMS from './styledDropdown/StyledOutboundSMS';
import StyledSuggestedAddress from './styledDropdown/StyledSuggestedAddress';

interface StyledAxonProps {
  axon: SearchResultData;
  index: number;
  mouseCursor: number;
  maxWidth: number;
  handleAxonSearchResult: HandleAxonSearchResult;
}

interface StyledCallProps {
  call: SearchResultData;
  index: number;
  mouseCursor: number;
  isPremiumUser: boolean;
  formatPhoneNumber: (phoneNumber: string) => string;
  handleSearchResult: (callerID: string) => void;
  maxWidth: number;
}

interface StyledOutboundSMSProps {
  call: SearchResultData;
  index: number;
  mouseCursor: number;
  formatPhoneNumber: (phoneNumber: string) => string;
  handleSearchOutboundSMS: (callerID: string) => void;
  maxWidth: number;
}

interface StyledSuggestedAddressProps {
  suggestion: SearchResult;
  index: number;
  mouseCursor: number;
  handleSelectedAddress: (suggestion: SearchResult) => void;
}

const SEARCH_INPUT_PADDING = 20;
const RESULT_CONTAINER_MAX_HEIGHT = '224px';

const defaultHandleSelectedAddress: (suggestion: SearchResult) => void = () =>
  undefined;

const StyledAxonComponent = StyledAxon as React.ComponentType<StyledAxonProps>;
const StyledCallComponent = StyledCall as React.ComponentType<StyledCallProps>;
const StyledOutboundSMSComponent =
  StyledOutboundSMS as React.ComponentType<StyledOutboundSMSProps>;
const StyledSuggestedAddressComponent =
  StyledSuggestedAddress as React.ComponentType<StyledSuggestedAddressProps>;

export const ResultsContainer = memo(
  ({
    displayResultContainer,
    hasError,
    mafError,
    isValidW3W,
    mouseCursor,
    formatPhoneNumber,
    handleSearchResult,
    handleSearchOutboundSMS,
    isPremiumUser,
    hasGisSearchAccess,
    resultContainerRef,
    gisSearchResults = [],
    filteredCallResults = [],
    filteredAxonResults = [],
    filteredOutboundSMSResults = [],
    top,
    left,
    width,
    handleSelectedAddress = defaultHandleSelectedAddress,
    handleAxonSearchResult,
  }: ResultsContainerProps) => {
    const maxWidth =
      document.documentElement.clientWidth - SEARCH_INPUT_PADDING;

    const combinedResults = useMemo(() => {
      return [
        ...filteredAxonResults,
        ...filteredCallResults,
        ...filteredOutboundSMSResults,
        ...gisSearchResults,
      ];
    }, [
      filteredAxonResults,
      filteredCallResults,
      filteredOutboundSMSResults,
      gisSearchResults,
    ]);

    const isVisible = useMemo(() => {
      return displayResultContainer && (hasError || combinedResults.length > 0);
    }, [combinedResults.length, displayResultContainer, hasError]);

    return isVisible ? (
      <StyledResultsContainer
        maxWidth={maxWidth}
        top={top}
        left={left}
        width={width}
      >
        {hasError && combinedResults.length === 0 ? (
          <ErrorWrapper isValidW3W={isValidW3W} mafError={mafError} />
        ) : (
          <OverflowWrapper
            maxHeight={RESULT_CONTAINER_MAX_HEIGHT}
            ref={resultContainerRef}
          >
            {combinedResults.reduce(
              (
                accumulator: React.ReactNode[],
                result: SearchResult,
                idx: number,
              ) => {
                if (result.data?.source === 'axon') {
                  const styledData = [
                    ...accumulator,
                    <StyledAxonComponent
                      key={idx}
                      axon={result.data}
                      index={idx}
                      mouseCursor={mouseCursor}
                      maxWidth={maxWidth}
                      handleAxonSearchResult={handleAxonSearchResult}
                    />,
                  ];
                  return styledData;
                } else if (
                  result.data?._datasetName === CALLS &&
                  (result.data?.core?.locations?.length ||
                    result.data?.core?.secondaryLocations?.length)
                ) {
                  const styledData = [
                    ...accumulator,
                    <StyledCallComponent
                      key={idx}
                      call={result.data}
                      index={idx}
                      mouseCursor={mouseCursor}
                      isPremiumUser={isPremiumUser}
                      formatPhoneNumber={formatPhoneNumber}
                      handleSearchResult={handleSearchResult}
                      maxWidth={maxWidth}
                    />,
                  ];
                  return styledData;
                } else if (result.data?.callerID) {
                  const styledData = [
                    ...accumulator,
                    <StyledOutboundSMSComponent
                      key={idx}
                      call={result.data}
                      index={idx}
                      mouseCursor={mouseCursor}
                      formatPhoneNumber={formatPhoneNumber}
                      handleSearchOutboundSMS={handleSearchOutboundSMS}
                      maxWidth={maxWidth}
                    />,
                  ];
                  return styledData;
                } else if (
                  result.text &&
                  result.magicKey &&
                  hasGisSearchAccess
                ) {
                  const styledData = [
                    ...accumulator,
                    <StyledSuggestedAddressComponent
                      key={idx}
                      suggestion={result}
                      index={idx}
                      mouseCursor={mouseCursor}
                      handleSelectedAddress={handleSelectedAddress}
                    />,
                  ];
                  return styledData;
                }
                return accumulator;
              },
              [],
            )}
          </OverflowWrapper>
        )}
      </StyledResultsContainer>
    ) : null;
  },
);
